import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UsersRound, UserPlus, Gamepad2, Bell, ShieldOff, Send, Search } from 'lucide-react';
import { usePlatform } from '../store';
import { api, json } from '../api';
import { Modal } from './Modal';
import { UserAvatar } from './UserAvatar';
import './friends.css';
type Person = { id: string; username: string; name: string; avatarUrl: string; online: boolean };
type Invite = {
  id: string;
  roomId: string;
  gameId: string;
  gameName: string;
  from: Person;
  to: Person;
  incoming: boolean;
  expiresAt: number;
};
type Social = {
  friends: Person[];
  requests: { person: Person; incoming: boolean }[];
  invites: Invite[];
  blocked: Person[];
  allowInvites: boolean;
};
export function FriendsButton() {
  const { data } = usePlatform();
  if (!data?.user) return null;
  return (
    <button
      className="friends-trigger"
      title="好友与游戏邀请"
      aria-label="好友"
      onClick={() => window.dispatchEvent(new CustomEvent('moyu:open-friends'))}
    >
      <UsersRound size={19} />
      <span>好友</span>
    </button>
  );
}
export function FriendsPanel() {
  const { data, notify } = usePlatform();
  const location = useLocation();
  const [open, setOpen] = useState(false),
    [social, setSocial] = useState<Social | null>(null),
    [tab, setTab] = useState<'friends' | 'inbox' | 'blocked'>('friends'),
    [username, setUsername] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [inviteRoom, setInviteRoom] = useState<{
      roomId?: string;
      partyId?: string;
      gameName: string;
    } | null>(null);
  const userId = data?.user?.id;
  const panelOpen = useRef(open);
  panelOpen.current = open;
  const refresh = async () => {
    const value = await api<Social>('/social');
    setSocial(value);
    return value;
  };
  useEffect(() => {
    setOpen(false);
    setSocial(null);
    setInviteRoom(null);
  }, [userId]);
  useEffect(() => {
    setInviteRoom(null);
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!userId) return;
    let disposed = false,
      pending = false;
    const read = async () => {
      if (document.hidden || pending || !panelOpen.current) return;
      pending = true;
      try {
        const value = await api<Social>('/social');
        if (!disposed) {
          setSocial(value);
        }
      } catch {
      } finally {
        pending = false;
      }
    };
    const show = (event: Event) => {
      setError('');
      setInviteRoom((event as CustomEvent).detail || null);
      setTab('friends');
      panelOpen.current = true;
      setOpen(true);
      void read();
    };
    const hide = () => setOpen(false);
    window.addEventListener('moyu:open-notifications', hide);
    window.addEventListener('moyu:social-changed', read);
    window.addEventListener('moyu:open-friends', show);
    window.addEventListener('focus', read);
    const timer = setInterval(read, 15000);
    void read();
    return () => {
      disposed = true;
      clearInterval(timer);
      window.removeEventListener('moyu:open-notifications', hide);
      window.removeEventListener('moyu:social-changed', read);
      window.removeEventListener('moyu:open-friends', show);
      window.removeEventListener('focus', read);
    };
  }, [userId]);
  const run = async (task: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await task();
      await refresh();
      window.dispatchEvent(new Event('moyu:notifications-refresh'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!userId || !open) return null;
  const person = (p: Person) => (
    <div className="friends-person">
      <UserAvatar src={p.avatarUrl} />
      <div>
        <strong>{p.name}</strong>
        <small>
          @{p.username}{' '}
          <span className={p.online ? 'friends-online' : ''}>{p.online ? '· 联机中' : ''}</span>
        </small>
      </div>
    </div>
  );
  return (
    <Modal title="岛上好友" onClose={() => setOpen(false)} className="friends-panel">
      <div className="friends-body">
        <div className="friends-intro">
          <span className="friends-island-icon">
            <UsersRound size={28} />
          </span>
          <div>
            <h3>{inviteRoom ? '叫上好友，一起开玩' : '一起玩，快乐加倍'}</h3>
            <p>
              {inviteRoom
                ? `正在邀请好友加入「${inviteRoom.gameName}」`
                : '添加岛上的伙伴，在游戏里发起邀请。'}
            </p>
          </div>
        </div>
        <nav className="friends-tabs" aria-label="好友分类">
          {(
            [
              ['friends', '我的好友'],
              ['inbox', '已发送'],
              ['blocked', '屏蔽名单'],
            ] as const
          ).map(([key, label]) => (
            <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </nav>
        {error && (
          <p className="friends-error" role="alert">
            {error}
          </p>
        )}
        {!social ? (
          <div className="friends-empty">正在打开好友列表…</div>
        ) : (
          <>
            {tab === 'friends' && (
              <>
                <form
                  className="friends-add"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void run(async () => {
                      await api('/social/requests', json('POST', { username }));
                      setUsername('');
                      notify('好友申请已发送');
                    });
                  }}
                >
                  <Search size={17} />
                  <input
                    value={username}
                    maxLength={64}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="输入好友的完整账号"
                    aria-label="好友账号"
                  />
                  <button className="button" disabled={busy || !username.trim()}>
                    <UserPlus size={16} />
                    添加
                  </button>
                </form>
                <div className="friends-list">
                  {social.friends.length ? (
                    social.friends.map((p) => (
                      <div className="friends-row" key={p.id}>
                        {person(p)}
                        <div className="friends-actions">
                          <button
                            className="button secondary"
                            onClick={() => {
                              setOpen(false);
                              window.dispatchEvent(
                                new CustomEvent('moyu:open-chat', { detail: p.id }),
                              );
                            }}
                          >
                            私信
                          </button>
                          {inviteRoom && (
                            <button
                              className="button"
                              disabled={busy}
                              onClick={() =>
                                void run(async () => {
                                  await api(
                                    inviteRoom.partyId
                                      ? '/multiplayer/party/invites'
                                      : '/social/invites',
                                    json('POST', {
                                      ...(inviteRoom.partyId
                                        ? { partyId: inviteRoom.partyId }
                                        : { roomId: inviteRoom.roomId }),
                                      userId: p.id,
                                    }),
                                  );
                                  notify('游戏邀请已发送');
                                })
                              }
                            >
                              <Send size={15} />
                              邀请
                            </button>
                          )}
                          <details>
                            <summary aria-label={`管理好友 ${p.name}`}>···</summary>
                            <div>
                              <button
                                disabled={busy}
                                onClick={() =>
                                  void run(() =>
                                    api(`/social/friends/${p.id}`, { method: 'DELETE' }),
                                  )
                                }
                              >
                                移除好友
                              </button>
                              <button
                                disabled={busy}
                                onClick={() =>
                                  void run(() =>
                                    api(`/social/blocks/${p.id}`, json('POST', { block: true })),
                                  )
                                }
                              >
                                屏蔽用户
                              </button>
                            </div>
                          </details>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="friends-empty">
                      <UsersRound size={32} />
                      <strong>还没有岛上好友</strong>
                      <span>输入伙伴的账号，发出第一份邀请。</span>
                    </div>
                  )}
                </div>
              </>
            )}
            {tab === 'inbox' && (
              <div className="friends-list">
                {social.invites
                  .filter((i) => !i.incoming)
                  .map((i) => (
                    <div className="friends-invitation" key={i.id}>
                      <Gamepad2 size={24} />
                      <div>
                        <strong>{i.gameName}</strong>
                        <p>
                          {i.incoming
                            ? `${i.from?.name || '好友'} 邀请你一起玩`
                            : `已邀请 ${i.to?.name || '好友'}`}
                        </p>
                        <small>
                          邀请有效至{' '}
                          {new Date(i.expiresAt).toLocaleTimeString('zh-CN', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </small>
                      </div>

                      <button
                        disabled={busy}
                        className="friends-secondary"
                        onClick={() =>
                          void run(() => api(`/social/invites/${i.id}`, { method: 'DELETE' }))
                        }
                      >
                        撤回
                      </button>
                    </div>
                  ))}
                {social.requests
                  .filter((r) => !r.incoming)
                  .map((r) => (
                    <div className="friends-row" key={r.person.id}>
                      {person(r.person)}

                      <>
                        <small>等待回应</small>
                        <button
                          className="friends-secondary"
                          disabled={busy}
                          onClick={() =>
                            void run(() =>
                              api(`/social/friends/${r.person.id}`, { method: 'DELETE' }),
                            )
                          }
                        >
                          撤回
                        </button>
                      </>
                    </div>
                  ))}
                {!social.requests.some((r) => !r.incoming) &&
                  !social.invites.some((i) => !i.incoming) && (
                    <div className="friends-empty">
                      <Bell size={32} />
                      <strong>暂无待回应的申请或邀请</strong>
                      <span>收到的申请与邀请请到通知中心查看。</span>
                    </div>
                  )}
              </div>
            )}
            {tab === 'blocked' && (
              <div className="friends-list">
                {social.blocked.length ? (
                  social.blocked.map((p) => (
                    <div className="friends-row" key={p.id}>
                      {person(p)}
                      <button
                        disabled={busy}
                        className="friends-secondary"
                        onClick={() =>
                          void run(() =>
                            api(`/social/blocks/${p.id}`, json('POST', { block: false })),
                          )
                        }
                      >
                        解除屏蔽
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="friends-empty">
                    <ShieldOff size={32} />
                    <strong>没有屏蔽的用户</strong>
                  </div>
                )}
              </div>
            )}
            <label className="friends-preferences">
              <span>
                <Bell size={16} />
                接收好友的游戏邀请
              </span>
              <input
                type="checkbox"
                checked={social.allowInvites}
                disabled={busy}
                onChange={(e) =>
                  void run(() =>
                    api('/social/preferences', json('PATCH', { allowInvites: e.target.checked })),
                  )
                }
              />
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}
